"use client";

import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuX } from "react-icons/lu";

import { Button } from "@/components/ui/button";
import { useMobile } from "@/hooks/use-mobile";

interface VoiceRecorderProps {
  onClose: () => void;
  onFinish: (file: File) => void;
}

export function VoiceRecorderOverlay({
  onClose,
  onFinish,
}: VoiceRecorderProps) {
  const [audioData, setAudioData] = useState<number[]>([]);
  const [duration, setDuration] = useState<number>(0); // Track recording duration in seconds
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const cancelledRef = useRef(false);
  const { hasMouse } = useMobile();
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Format seconds to MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        streamRef.current = stream;

        let mimeType;

        if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4"; // Best for Safari
        } else if (MediaRecorder.isTypeSupported("audio/webm")) {
          mimeType = "audio/webm"; // Best for Chrome, Edge, Firefox
        } else {
          mimeType = ""; // Let browser choose
        }

        // Set up audio context and analyzer
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        analyserRef.current = analyser;
        analyser.fftSize = 256;

        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;
        source.connect(analyser);

        // Set up media recorder
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType,
        });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          if (cancelledRef.current) return;

          const audioBlob = new Blob(audioChunksRef.current, {
            type: mimeType,
          });
          audioChunksRef.current = [];

          const name = new Date().toISOString().replace(/[:.]/g, "-");
          const file = new File([audioBlob], `Recording ${name}`, {
            type: mimeType,
          });
          onFinishRef.current(file);
        };

        // Start recording
        mediaRecorder.start();

        // Start duration timer
        setDuration(0);
        timerRef.current = setInterval(() => {
          setDuration((prev) => prev + 1);
        }, 1000);

        // Set up visualization loop with higher sensitivity
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.minDecibels = -85; // Increase sensitivity to quiet sounds
        analyser.maxDecibels = -30;
        analyser.smoothingTimeConstant = 0.5; // Lower value = more responsive

        const updateVisualization = () => {
          analyser.getByteFrequencyData(dataArray);

          // Convert to array and normalize values between 0-1
          const normalizedData = Array.from(dataArray).map(
            (value) => value / 255,
          );
          setAudioData(normalizedData);

          animationFrameRef.current =
            requestAnimationFrame(updateVisualization);
        };

        updateVisualization();
      } catch (error) {
        console.error("Error accessing microphone:", error);
        onCloseRef.current();
      }
    };

    startRecording();

    return () => {
      // Clean up
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Clear the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const handleFinish = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    onClose();
  };

  const handleCancel = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    audioChunksRef.current = []; // Discard recorded audio
    onClose();
  };

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      <DismissableLayer
        disableOutsidePointerEvents
        className="
          pointer-events-auto fixed inset-0 z-300 flex flex-col items-center
          justify-center bg-black/90
        "
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          cancelledRef.current = true;
          handleCancel();
        }}
        onClick={handleFinish}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="contents"
        >
          <div className="absolute top-12 flex flex-col items-center gap-4">
            <motion.div
              className="text-3xl font-medium text-white"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ repeat: Number.POSITIVE_INFINITY, duration: 2 }}
            >
              Recording...
            </motion.div>

            {/* Duration timer */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-mono text-lg text-white/80"
            >
              {formatTime(duration)}
            </motion.div>
          </div>

          <div className="relative">
            <AudioVisualizer audioData={audioData} />
          </div>

          <motion.div
            className="absolute bottom-28 text-3xl font-medium text-white/80"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            {hasMouse ? "Click" : "Tap"} to Finish
          </motion.div>

          <div
            className="absolute bottom-10 flex w-full justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="outline"
              size="lg"
              onClick={() => {
                cancelledRef.current = true;
                handleCancel();
              }}
              className="
                border-white/20 bg-transparent text-white
                hover:bg-white/10 hover:text-white
              "
            >
              <LuX className="mr-2 size-4" />
              Cancel
            </Button>
          </div>
        </motion.div>
      </DismissableLayer>
    </AnimatePresence>,
    document.body,
  );
}

interface AudioVisualizerProps {
  audioData: number[];
}

export function AudioVisualizer({ audioData }: AudioVisualizerProps) {
  const maxRadius = 120;
  const minRadius = 80;
  const sensitivity = 5;

  // Get frequency bands for more detailed visualization
  const getBands = () => {
    if (!audioData.length) return { bass: 0, mid: 0, treble: 0 };

    // Split the frequency data into bass, mid, and treble ranges
    const bassRange = audioData.slice(0, Math.floor(audioData.length * 0.1));
    const midRange = audioData.slice(
      Math.floor(audioData.length * 0.1),
      Math.floor(audioData.length * 0.5),
    );
    const trebleRange = audioData.slice(Math.floor(audioData.length * 0.5));

    // Calculate average values for each range
    const bass =
      (bassRange.reduce((sum, val) => sum + val, 0) / bassRange.length) *
      sensitivity;
    const mid =
      (midRange.reduce((sum, val) => sum + val, 0) / midRange.length) *
      sensitivity;
    const treble =
      (trebleRange.reduce((sum, val) => sum + val, 0) / trebleRange.length) *
      sensitivity;

    return { bass, mid, treble };
  };

  const { bass, mid, treble } = getBands();

  // Calculate overall amplitude (weighted more towards bass for voice)
  const amplitude = bass * 0.6 + mid * 0.3 + treble * 0.1;

  // More dynamic radius calculation
  const radius = minRadius + amplitude * (maxRadius - minRadius);

  // Set initial size for perfect circle from the start
  const initialSize = minRadius * 2;
  const backgroundSize = maxRadius * 2.2;
  const ringSize = minRadius + maxRadius;

  return (
    <div className="relative flex items-center justify-center">
      {/* Background glow - reduced blur */}
      <motion.div
        initial={{
          scale: 0.9,
          filter: `blur(2px)`,
          width: backgroundSize,
          height: backgroundSize,
          borderRadius: "9999px", // Ensure it's a circle from the start
        }}
        animate={{
          scale: 0.9 + amplitude * 0.4,
          filter: `blur(${2 + amplitude * 6}px)`,
        }}
        transition={{ type: "spring", damping: 10, stiffness: 300 }}
        className="absolute"
      >
        <div
          className="
            size-full rounded-full bg-primary bg-linear-to-r opacity-40
          "
        />
      </motion.div>

      {/* Main circle */}
      <motion.div
        initial={{
          width: initialSize,
          height: initialSize,
          borderRadius: "9999px", // Ensure it's a circle from the start
        }}
        animate={{
          width: radius * 2,
          height: radius * 2,
        }}
        transition={{ type: "spring", damping: 8, stiffness: 400 }}
        className="
          relative flex items-center justify-center rounded-full bg-primary
        "
      >
        {/* Inner circle */}
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{
            scale: 0.9 + bass * 0.3,
          }}
          transition={{ type: "spring", damping: 5, stiffness: 500 }}
          className="size-3/4 rounded-full bg-white/20"
        />
      </motion.div>

      {/* Simplified reactive ring */}
      <motion.div
        initial={{
          width: ringSize,
          height: ringSize,
          borderRadius: "9999px", // Ensure it's a circle from the start
          opacity: 0.2,
        }}
        animate={{
          width: ringSize * (1 + mid * 0.3),
          height: ringSize * (1 + mid * 0.3),
          opacity: 0.2 + amplitude * 0.6,
        }}
        transition={{ type: "spring", damping: 8, stiffness: 400 }}
        className="absolute rounded-full bg-primary/30"
      />
    </div>
  );
}
